import React, { createContext, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

import { useParamValues, useQueryValues } from 'hooks/url.hook';
import { toQueryString } from 'utils';

type DashboardPageContextType = {
  /**
   * buildingId which comes from url param /<buildingId>
   */
  buildingId: number;

  /**
   * start and end date strings from url query ?start=<startDate>&end=<endDate>
   *
   * Note: string format follows ISO date string format to be considered backend side timezone.
   * e.g: 2022-06-20T01:46:04.936Z
   */
  start: string;
  end: string;
};

const defaultDashboardPageContextValue = {
  buildingId: 0,
  start: '',
  end: '',
};

export const DashboardPageContext = createContext<DashboardPageContextType>(defaultDashboardPageContextValue);

interface DashboardPageContextProviderProps {
  children?: any;
}

export const DashboardPageContextProvider = ({ children = null }: DashboardPageContextProviderProps) => {
  const navigate = useNavigate();
  const { start, end } = useQueryValues(['start', 'end']);
  const { buildingId: buildingIdStr } = useParamValues(['buildingId']);

  const buildingId = buildingIdStr ? Number(buildingIdStr) : null;

  useEffect(() => {
    if (!start && !end) {
      const start = dayjs().subtract(30, 'days').toISOString();
      const end = dayjs().toISOString();

      const qs = toQueryString({ start, end });

      navigate(qs ? `?${qs}` : '');
    }
  }, [start, end]);

  return (
    <DashboardPageContext.Provider value={{ buildingId: buildingId as number, start, end }}>
      {children}
    </DashboardPageContext.Provider>
  );
};

export const useDashboardPageContext = () => {
  return useContext(DashboardPageContext);
};
